
import React, { memo, useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as StartIcon,
    Stop as StopIcon,
    Message as MessageIcon,
    Chat as ChatIcon,
    Computer as ComputerIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import * as assistantService from '../services/assistantService';

// Assistant status badge colors
const statusColors = {
    active: 'success',
    inactive: 'error'
};

const AssistantCard = memo(({
                                assistant,
                                onEdit,
                                onDelete,
                                onStart,
                                onStop
                            }) => {
    // Функция для получения информации об инстансах
    const getInstancesInfo = () => {
        console.log('Assistant data for', assistant.name, ':', assistant);
        console.log('assistant.Instances:', assistant.Instances);

        // Используем данные об инстансах если они есть
        if (assistant.Instances && Array.isArray(assistant.Instances)) {
            console.log('Using assistant.Instances:', assistant.Instances);
            return assistant.Instances.map(instance => ({
                id: instance.id,
                alias: instance.alias,
                phone: instance.phone,
                status: instance.status
            }));
        }

        console.log('No instances found for assistant:', assistant.name);
        return [];
    };

    // Функция для определения цвета бейджа по статусу инстанса
    const getInstanceBadgeColor = (status) => {
        const activeStatuses = ['connected', 'active', 'authorized', 'running'];
        const blockedStatuses = ['blocked', 'banned', 'suspended', 'unauthorized'];

        if (activeStatuses.includes(status?.toLowerCase())) {
            return '#4caf50'; // зеленый для активных
        } else if (blockedStatuses.includes(status?.toLowerCase())) {
            return '#f44336'; // красный для заблокированных и неавторизованных
        } else {
            return '#ff9800'; // оранжевый для остальных статусов
        }
    };

    const instancesInfo = getInstancesInfo();

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        {assistant.name || `Assistant #${assistant.id}`}
                    </Typography>
                    <Box>
                        <Chip
                            label={assistant.status === 'active' ? 'Active' : 'Inactive'}
                            color={statusColors[assistant.status === 'active' ? 'active' : 'inactive']}
                            size="small"
                        />
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Created: {format(new Date(assistant.created_at), 'MMM d, yyyy')}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Temperature: {assistant.temperature || 0.7}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Delay: {assistant.response_delay || 0} seconds
                </Typography>

                {/* Информация об инстансах */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary" component="span">
                        Instances:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {instancesInfo.length > 0 ? (
                            instancesInfo.map((instance, index) => (
                                <Chip
                                    key={instance.id || index}
                                    label={instance.phone || instance.alias || `#${index + 1}`}
                                    size="small"
                                    title={`Instance ${instance.alias || instance.id}: ${instance.status || 'unknown'} ${instance.phone ? `(${instance.phone})` : ''}`}
                                    sx={{
                                        backgroundColor: getInstanceBadgeColor(instance.status),
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        height: '20px',
                                        '& .MuiChip-label': { px: 0.75 }
                                    }}
                                />
                            ))
                        ) : (
                            <Chip
                                label="No instances"
                                size="small"
                                sx={{
                                    backgroundColor: '#9e9e9e',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    height: '20px',
                                    '& .MuiChip-label': { px: 0.75 }
                                }}
                            />
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Statistics Section */}
                <Typography variant="subtitle2" gutterBottom>
                    Statistics
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MessageIcon fontSize="small" color="primary" />
                        <Typography variant="body2">
                            IN: {assistant.message_in_count} | OUT: {assistant.message_out_count} messages
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon fontSize="small" color="primary" />
                        <Typography variant="body2">
                            {assistant.chat_count} chats involved
                        </Typography>
                    </Box>
                </Box>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Box>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => onEdit(assistant)}
                            title={assistant.status === 'active' ? "View (cannot edit while assistant is running)" : "Edit"}
                            disabled={false} // Always allow viewing, editing will be restricted in the dialog
                        >
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => onDelete(assistant)}
                            title={assistant.status === 'active' ? "Cannot delete while assistant is running" : "Delete"}
                            disabled={assistant.status === 'active'}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                    <Box>
                        {assistant.status === 'active' ? (
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => onStop(assistant)}
                                title="Stop"
                            >
                                <StopIcon />
                            </IconButton>
                        ) : (
                            <IconButton
                                color="success"
                                size="small"
                                onClick={() => onStart(assistant)}
                                title="Start"
                            >
                                <StartIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
});

AssistantCard.displayName = 'AssistantCard';

export default AssistantCard;